export { PRODUCTS_SCOPE } from "./navigation.ts";

import type { StateClassKey } from "@tdelivr/design-tokens";

import { type BoardRow, type DetailField } from "./board.ts";
import { money } from "./money.ts";

/**
 * What the products board does to what COM-02 returns, away from any rendering.
 *
 * Built to `22 WP21 Merchant Portal.dc.html`, page `menu`:
 *
 * ```
 * menu:['Products and menu','COM-02','What customers can buy, with prices, photos and options.',
 *   [['Products','214','done','live'],['Hidden','9','closed',''],['No photo','12','caution',''],
 *    ['Price changes pending','2','waiting','effective tomorrow']],
 *   ['Product','Category','Price'],
 *   [['Avocado, Hass','Fruit and veg','KES 60.00 each','done','live'],
 *    ['Sukuma wiki','Fruit and veg','KES 30.00 bunch','done','live'],
 *    ['Eggs, tray of 30','Dairy and eggs','KES 540.00','caution','no photo'],
 *    ['Mango juice 1 L','Drinks','KES 130.00 → 140.00','waiting','price from tomorrow']],
 *   'info','Price changes apply to new carts only; carts already open keep the old price for their hold time.']
 * ```
 *
 * ## This screen was ready for seven cohorts and had nowhere to be drawn
 *
 * `console-screen-support.test.ts` recorded it as the one of the forty-two that was **ready here
 * and not drawn**, with one reason: "whose channel has no application at all". COM-02's listing has
 * been published since the cohort that served the finance boards. This portal is the application.
 *
 * ## What is drawn from data and what is not
 *
 * | drawn                            | source                                                     |
 * | -------------------------------- | ---------------------------------------------------------- |
 * | the rows                         | COM-02 `GET /v1/tenants/{tenantId}/products`               |
 * | `Product` — `Avocado, Hass`      | `name`, a real name — the one on this portal that is       |
 * | `Category` — `Fruit and veg`     | `categoryId`. **The name is inside COM-01's catalogue**    |
 * | `Price` — `KES 60.00`            | `price`, with its currency and full minor units            |
 * | `Price` — `each`, `bunch`        | **nothing** — no unit is published                         |
 * | `KES 130.00 → 140.00`            | **nothing** — no future price and no effective date        |
 * | `caution · no photo`             | **nothing** — COM-02 publishes no photo field at all       |
 * | the four tiles                   | no listing publishes a total (R-04); no tile row here      |
 *
 * ## The category is the one absence worth arguing about
 *
 * COM-02 publishes `categoryId` and `catalogId`. The *name* — `Fruit and veg` — is a category entry
 * inside the catalogue that `catalogId` names, and COM-01's catalogue read answers it. So unlike
 * most absences on this platform, this one **is** reachable: read the catalogue, find the category,
 * draw the name.
 *
 * It is not drawn, and the reason is the rule rather than the effort. A board that fetched a
 * catalogue per distinct `catalogId` to fill one column is a second service on one screen, which is
 * the fan-out Document 17 WEB-0743 tolerates for one listing per screen and not for a column — and
 * it is the exact defect the dispatch console's live map was rebuilt to remove. The honest
 * resolution is a field: COM-02 publishing `categoryName` beside `categoryId`, which is one
 * denormalised column on a read model. Recorded as the thing to ask for rather than worked around.
 */

/** COM-02's published status enum, as one name. */
export type ProductStatus = "draft" | "active" | "suspended" | "retired";

/** The money COM-02 publishes on a product. */
export interface ProductPrice {
  readonly amountMinor: number;
  readonly currency: string;
}

/** One product, as COM-02's listing publishes it. */
export interface Product {
  readonly productId: string;
  readonly tenantId: string;
  readonly merchantId: string;
  readonly catalogId: string;
  readonly categoryId: string;
  readonly sku: string;
  readonly name: string;
  readonly description: string;
  readonly price: ProductPrice;
  readonly status: ProductStatus;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/**
 * The state class and the word, from `status`.
 *
 * | status      | class     | word         | where the word comes from                  |
 * | ----------- | --------- | ------------ | ------------------------------------------ |
 * | `active`    | `done`    | `live`       | the design's `done · live` row             |
 * | `draft`     | `idle`    | `draft`      | the contract's word; the design draws none |
 * | `suspended` | `closed`  | `hidden`     | the design's `Hidden · 9` tile             |
 * | `retired`   | `closed`  | `retired`    | the contract's word                        |
 *
 * **`suspended` reads `hidden`, which is the design's word and the merchant's.** A suspended
 * product is one customers cannot see, and that is what the design's tile calls it. The contract's
 * own word is on the badge's title.
 *
 * **`draft` is `idle` and not `waiting`.** The design's `waiting` class is for a row whose next
 * move belongs to somebody outside the console — its own `waiting` row is a price change due
 * tomorrow — and a draft product is waiting on this merchant, who can publish it whenever they
 * like. `idle` is the design's class for "exists; nothing has happened yet".
 */
export function stateOf(product: Product): { classKey: StateClassKey; label: string } {
  switch (product.status) {
    case "active":
      return { classKey: "done", label: "live" };
    case "draft":
      return { classKey: "idle", label: "draft" };
    case "suspended":
      return { classKey: "closed", label: "hidden" };
    case "retired":
      return { classKey: "closed", label: "retired" };
  }
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly Product[]): readonly string[] {
  return [...new Set(rows.map((row) => stateOf(row).label))];
}

/**
 * The design's `Price` cell: the currency code and the full minor units.
 *
 * CLAUDE.md section 2, and on this board it is what a customer will be charged. The design writes a
 * unit after it — `each`, `bunch` — and COM-02 publishes none, so the cell stops at the money. The
 * design's `KES 130.00 → 140.00` is a price change due tomorrow, which needs a future price and an
 * effective date that nothing publishes either.
 */
export function priceCell(product: Product): string {
  return money(product.price.amountMinor, product.price.currency);
}

/** One row, with its detail panel. */
export function productRow(product: Product): BoardRow {
  const state = stateOf(product);

  return {
    id: product.productId,
    cells: [
      {
        text: product.name,
        title: `SKU ${product.sku}. ${product.description}`,
      },
      {
        text: product.categoryId,
        /*
         * Mono, which `board.ts` calls its extension of the design's own rule: a published
         * identifier a page is drawing in place of a name. The design writes `Fruit and veg`.
         */
        mono: true,
        title:
          "The category identifier COM-02 holds. The name lives as a category entry inside COM-01's catalogue — reachable, and not read here, because a catalogue fetch per row to fill one column is a second service on one board. The honest fix is COM-02 publishing categoryName beside categoryId. See R-35.",
      },
      {
        text: priceCell(product),
        title:
          "The design writes a unit after the price — each, bunch — and COM-02 publishes none. Its `KES 130.00 → 140.00` is a price change due tomorrow, and nothing publishes a future price or an effective date.",
      },
    ],
    state: {
      ...state,
      title: `COM-02 publishes status ${product.status}. ${describeStatus(product.status)}`,
    },
    // The design tints `human` and `failed` and this board reaches neither: no COM-02 status maps
    // to either, because a product cannot fail and does not wait on a person.
    detail: detailFor(product),
    history: historyFor(product),
    // The design's own form glyph for this page.
    glyph: "restaurant_menu",
  };
}

function describeStatus(status: ProductStatus): string {
  switch (status) {
    case "active":
      return "Customers can buy it.";
    case "draft":
      return "It exists and customers cannot see it. Publishing is this merchant's to do, which is why it is drawn idle rather than waiting.";
    case "suspended":
      return "Hidden from customers. The design's own word for this is `hidden`; the contract's is `suspended`.";
    case "retired":
      return "Withdrawn for good. COM-02 publishes no reason and no date beyond the row's own updatedAt.";
  }
}

function detailFor(product: Product): readonly DetailField[] {
  return [
    { label: "SKU", value: product.sku, mono: true },
    { label: "Description", value: product.description },
    { label: "Catalogue", value: product.catalogId, mono: true },
    { label: "Category", value: product.categoryId, mono: true },
    { label: "Category name", value: "—", absent: true },
    { label: "Photo", value: "—", absent: true },
    { label: "Unit", value: "—", absent: true },
  ];
}

function historyFor(product: Product): readonly DetailField[] {
  return [
    { label: "Created", value: product.createdAt, mono: true },
    { label: "Last changed", value: product.updatedAt, mono: true },
    { label: "Version", value: `v${String(product.version)}`, mono: true },
  ];
}

/**
 * Why the designed primary action cannot be sent.
 *
 * `POST /v1/tenants/{tenantId}/products` is **public** — unlike every other refused command on this
 * portal — so this refusal is about the body rather than the address, and it is the narrowest of
 * the three.
 *
 * The design's `Add a product` collects eight fields: `Photo`, `Name`, `Category`, `Price (KES)`,
 * `Unit`, `Description`, `Options` and `Age-restricted`. COM-02's published body takes a name, a
 * description, a SKU, a price, a catalogue and a category — so **four of the eight have no field on
 * it**: the photo, the unit, the options and the age restriction. The first and the last are the
 * two a customer would notice most.
 *
 * Sending the four that fit and dropping the rest is what CLAUDE.md section 2 forbids in the other
 * direction — it is not inventing a payload, it is silently discarding what a merchant typed. A
 * form that takes a photo and does not store it is worse than one that is refused.
 */
export const ADD_REFUSAL =
  "Not available. COM-02's published body has no field for the form's Photo, Unit, Options or Age-restricted — four of its eight. The route is public; what is missing is the body, and sending half the form would silently discard what a merchant typed.";

/**
 * What this board owes a merchant beyond its rows.
 *
 * It names the category absence, because that column is a third of the grid and an identifier where
 * a name belongs is the one cell on this board a merchant cannot read.
 */
export function listingNote(rows: readonly Product[] | undefined): string | undefined {
  if (rows === undefined) return undefined;
  const merchants = new Set(rows.map((row) => row.merchantId)).size;
  return (
    `${String(rows.length)} product(s) fetched, newest first — COM-02 publishes a cursor and no ` +
    `count. The Category column shows the identifier COM-02 holds: the name is a category entry ` +
    `inside the catalogue, and reading a catalogue per row to fill one column would put a second ` +
    `service behind this board. COM-02 publishing categoryName beside categoryId is the fix. ` +
    (merchants > 1
      ? `These are the tenant's products across ${String(merchants)} merchants, not this merchant's ` +
        `alone: COM-02's listing is addressed by tenant. A merchant-scoped sibling is what would ` +
        `make this board theirs, and filtering here would hide rows without the service agreeing.`
      : `COM-02's listing is addressed by tenant rather than by merchant, so a second merchant on ` +
        `this tenant would appear here too.`)
  );
}
